import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Loader2, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { PublicFooter } from "@/components/PublicFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";

export default function Login() {
  const [email, setEmail]               = useState("");
  const [password, setPassword]         = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [magicLoading, setMagicLoading] = useState(false);
  const [magicSent, setMagicSent]       = useState(false);

  const { login } = useAuth();
  const navigate  = useNavigate();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast({ title: "Fill in both fields", description: "Email and password are required.", variant: "destructive" });
      return;
    }
    setLoading(true);
    const { error } = await login(email, password);
    setLoading(false);
    if (error) {
      toast({ title: "Sign in failed", description: error, variant: "destructive" });
    } else {
      navigate("/dashboard");
    }
  };

  const handleMagicLink = async () => {
    const target = email.trim();
    if (!target || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(target)) {
      toast({ title: "Enter your email first", description: "Type your email address above, then click Send magic link.", variant: "destructive" });
      return;
    }
    setMagicLoading(true);
    const { error } = await supabase.auth.signInWithOtp({
      email: target,
      options: { emailRedirectTo: `${window.location.origin}/dashboard` },
    });
    setMagicLoading(false);
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      setMagicSent(true);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-sm space-y-8">

          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            className="flex justify-center"
          >
            <Link to="/" className="relative flex items-center justify-center group">
              <div className="absolute inset-0 rounded-3xl bg-primary/15 blur-2xl scale-125" />
              <div className="relative rounded-2xl bg-card border border-border/40 shadow-2xl p-3 backdrop-blur-sm group-hover:scale-[1.03] transition-transform">
                <img src="/logo.png" alt="Stratakey" className="h-16 w-auto object-contain rounded-xl" />
              </div>
            </Link>
          </motion.div>

          {/* Heading */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12, duration: 0.35 }}
            className="text-center space-y-1"
          >
            <h1 className="text-2xl font-bold tracking-tight">Welcome back</h1>
            <p className="text-sm text-muted-foreground">Sign in to your Stratakey account</p>
          </motion.div>

          {/* Form card */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.22, duration: 0.35 }}
            className="rounded-xl border bg-card shadow-sm p-6 space-y-4"
          >
            {/* Email */}
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  className="pl-9 h-10"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setMagicSent(false); }}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs">Password</Label>
                <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="pl-9 pr-10 h-10"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  autoComplete="current-password"
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit(e as any)}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(v => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button className="w-full h-10" onClick={handleSubmit} disabled={loading}>
              {loading
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in…</>
                : "Sign in"
              }
            </Button>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-card px-3 text-[11px] text-muted-foreground">or</span>
              </div>
            </div>

            {/* Magic link */}
            {magicSent ? (
              <div className="flex flex-col items-center gap-2 py-1 text-center">
                <CheckCircle2 className="h-7 w-7 text-green-500" />
                <p className="text-sm font-medium">Magic link sent!</p>
                <p className="text-xs text-muted-foreground">
                  Check <strong>{email}</strong> for a one-click sign-in link.
                </p>
                <button
                  type="button"
                  className="text-xs text-primary hover:underline mt-0.5"
                  onClick={() => setMagicSent(false)}
                >
                  Resend or use a different email
                </button>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full h-10"
                onClick={handleMagicLink}
                disabled={magicLoading}
              >
                {magicLoading
                  ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                  : <><Mail className="mr-2 h-4 w-4" /> Send magic link</>
                }
              </Button>
            )}
          </motion.div>

          {/* Footer links */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.3 }}
            className="text-center space-y-1.5 pb-4"
          >
            <p className="text-xs text-muted-foreground">
              Don't have an account?{" "}
              <Link to="/signup" className="font-medium text-primary hover:underline">Sign up</Link>
            </p>
            <p className="text-xs text-muted-foreground">
              Have an invitation?{" "}
              <Link to="/accept-invite" className="font-medium text-primary hover:underline">Accept invite</Link>
            </p>
          </motion.div>

        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
